import { Pencil } from "lucide-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation } from "randmar-api-client";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Switch, TableCell, TableRow, useToast } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { PurchaseOrderPartNumber } from "../../stores/document/Document";
import ProductDetailsModal from '../products/ProductDetailsModal';
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";
import CurrencyUtils from "../util/CurrencyUtils";
import InlineSpinner from "../util/InlineSpinner";

interface IProps {
  partNumber: PurchaseOrderPartNumber;
  purchaseOrderNumber: string;
}

const PurchaseOrderPartNumberRow: React.FC<IProps> = ({ partNumber, purchaseOrderNumber }) => {
  const { applicationId } = useManufacturer();
  const [updateStandardCost, setUpdateStandardCost] = React.useState(false);
  const [openUpdate, setOpenUpdate] = React.useState(false);
  const [count, setCount] = React.useState(partNumber.quantity || 0);
  const [openEditUnitPrice, setOpenEditUnitPrice] = React.useState(false);
  const [unitPrice, setUnitPrice] = React.useState(partNumber.unitPrice || 0);
  const { toast } = useToast();

  const [editItem, { isLoading: isEditing }] = usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation();

  const hasQuantityChanged = count !== (partNumber.quantity || 0);
  const hasUnitPriceChanged = unitPrice !== (partNumber.unitPrice || 0);

  const handleUnitPriceChange = (raw: string) => {
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setUnitPrice(0);
      return;
    }
    setUnitPrice(parseFloat(parsed.toFixed(2)));
  };

  const handleOnCountChanged = (raw: string) => {
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setCount(0);
      return;
    }
    setCount(parsed);
  };

  const handleUnitPriceConfirm = async () => {
    try {
      await editItem({
        routeManufacturerId: applicationId,
        purchaseOrderNumber,
        randmarSku: partNumber.randmarSKU || "",
        unitPrice,
        editStandardCost: updateStandardCost
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Unit price updated successfully." });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not update unit price." });
    }

    setOpenEditUnitPrice(false);
  };

  const handleConfirm = async (operation: string): Promise<void> => {
    let newValue = 0;
    let messageAdded = `${partNumber.mpn} removed from purchase order ${purchaseOrderNumber}`;
    let messageNotAdded = `${partNumber.mpn} could not be removed from purchase order ${purchaseOrderNumber}`;

    if (operation === "update") {
      const quantityDifference = count - (partNumber.quantity || 0);
      newValue = count;

      if (quantityDifference > 0) {
        messageAdded = `${quantityDifference} x ${partNumber.mpn} added to purchase order ${purchaseOrderNumber}`;
        messageNotAdded = `${quantityDifference} x ${partNumber.mpn} could not be added to purchase order ${purchaseOrderNumber}`;
      } else {
        messageAdded = `${quantityDifference * -1} x ${partNumber.mpn} removed from purchase order ${purchaseOrderNumber}`;
        messageNotAdded = `${quantityDifference * -1} x ${partNumber.mpn} could not be removed from purchase order ${purchaseOrderNumber}`;
      }
    }

    if (newValue !== partNumber.quantity || operation === "delete") {
      try {
        await editItem({
          routeManufacturerId: applicationId,
          purchaseOrderNumber,
          randmarSku: partNumber.randmarSKU || "",
          quantity: newValue
        }).unwrap();
        toast({ variant: "success", title: "Success", description: messageAdded });
      } catch {
        toast({ variant: "destructive", title: "Error", description: messageNotAdded });
      }
    }
    setOpenUpdate(false);
  };

  return (
    <TableRow
      className={[
        "h-14 border-b border-gray-100 hover:bg-gray-50/60",
        partNumber.category === "GRY" ? "bg-amber-50" : "",
      ].filter(Boolean).join(" ")}
      aria-busy={isEditing}
    >
      <TableCell>
        <div className="flex flex-col">
          <Link
            to={Path.product(applicationId, partNumber.randmarSKU)}
            className="font-semibold text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
          >
            {partNumber.mpn || partNumber.randmarSKU}
          </Link>
          <span className="text-sm text-slate-500">{partNumber.description}</span>
        </div>
      </TableCell>
      <TableCell className="text-center">
        <Input
          className="w-24 text-center"
          type="number"
          min={0}
          value={count}
          inputMode="numeric"
          disabled={isEditing}
          aria-label={`Quantity for ${partNumber.mpn ?? partNumber.randmarSKU ?? "line item"}`}
          onChange={(e) => handleOnCountChanged(e.target.value)}
        />
      </TableCell>
      <TableCell className="text-center">
        {hasQuantityChanged ? (
          <Button
            id="ButtonConfirmItemChange"
            size="sm"
            onClick={() => setOpenUpdate(true)}
            disabled={openUpdate || isEditing}
            className="rounded-xl"
            aria-label={`Update quantity for ${partNumber.mpn ?? partNumber.randmarSKU ?? "line item"}`}
          >
            Update
          </Button>
        ) : null}

        <Dialog open={openUpdate} onOpenChange={(isOpen) => !isOpen && !isEditing && setOpenUpdate(false)}>
          <DialogContent className="sm:max-w-[425px]" aria-busy={isEditing}>
            <DialogHeader>
              <DialogTitle className="text-center">
                Update {partNumber.mpn ?? partNumber.randmarSKU} quantity?
              </DialogTitle>
            </DialogHeader>

            {isEditing ? (
              <div className="flex min-h-[80px] items-center justify-center">
                <InlineSpinner size="lg" tone="accent" decorative />
              </div>
            ) : (
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenUpdate(false)} className="rounded-xl">
                  Cancel
                </Button>
                <Button onClick={() => handleConfirm("update")} className="rounded-xl">
                  Confirm
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center justify-center gap-2">
          <span className="text-slate-700">
            <FormattedNumber {...CurrencyUtils.format} value={partNumber.unitPrice || 0} />
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-xl"
            onClick={() => {
              setUnitPrice(partNumber.unitPrice || 0);
              setUpdateStandardCost(false);
              setOpenEditUnitPrice(true);
            }}
            disabled={isEditing}
            aria-label={`Edit unit price for ${partNumber.mpn ?? partNumber.randmarSKU ?? "line item"}`}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        <Dialog open={openEditUnitPrice} onOpenChange={(isOpen) => !isOpen && !isEditing && setOpenEditUnitPrice(false)}>
          <DialogContent className="sm:max-w-[520px]" aria-busy={isEditing}>
            <DialogHeader>
              <DialogTitle className="text-center">
                Edit unit price ({purchaseOrderNumber})
              </DialogTitle>
            </DialogHeader>

            {isEditing ? (
              <div className="flex min-h-[80px] items-center justify-center">
                <InlineSpinner size="lg" tone="accent" decorative />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor={`unit-price-${partNumber.randmarSKU}`}>
                    Unit price
                  </Label>
                  <Input
                    id={`unit-price-${partNumber.randmarSKU}`}
                    className="w-40"
                    type="number"
                    min={0}
                    step="0.01"
                    value={unitPrice}
                    onChange={(e) => handleUnitPriceChange(e.target.value)}
                  />
                </div>

                <div className="flex items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Standard cost</p>
                    <p className="text-[13px] text-slate-600">
                      Optionally update standard cost to match this new unit price.
                    </p>
                  </div>
                  <Switch
                    checked={updateStandardCost}
                    onCheckedChange={setUpdateStandardCost}
                    disabled={!hasUnitPriceChanged || unitPrice <= 0}
                  />
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenEditUnitPrice(false)} className="rounded-xl">
                    Cancel
                  </Button>
                  <Button onClick={handleUnitPriceConfirm} disabled={unitPrice <= 0} className="rounded-xl">
                    Confirm
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </TableCell>

      <TableCell className="text-center text-slate-700">
        <FormattedNumber {...CurrencyUtils.format} value={(partNumber.unitPrice || 0) * (partNumber.quantity || 0)} />
      </TableCell>
      <TableCell className="text-center"><ProductDetailsModal randmarSKU={partNumber.randmarSKU || ""} /></TableCell>

      <TableCell className="text-center">
        <ButtonWithDialogConfirm
          fab={true}
          onConfirmation={() => handleConfirm("delete")}
          buttonTitle={`Remove ${partNumber.mpn} from ${purchaseOrderNumber}`}
        />
      </TableCell>
    </TableRow >
  );
};

export default PurchaseOrderPartNumberRow;
